/**
 * TreatX Clinic Representative Data Context
 * Manages patient data for clinic representatives (post-sale operations)
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

const ClinicRepresentativeDataContext = createContext();

export const useClinicRepData = () => {
  const context = useContext(ClinicRepresentativeDataContext);
  if (!context) {
    throw new Error('useClinicRepData must be used within ClinicRepresentativeDataProvider');
  }
  return context;
};

// Operational status stages
export const OPERATIONAL_STATUSES = {
  ARRIVAL_PENDING: { label: 'Arrival Pending', color: 'bg-amber-100 text-amber-700' },
  ARRIVED: { label: 'Arrived', color: 'bg-blue-100 text-blue-700' },
  PICKED_UP: { label: 'Picked Up', color: 'bg-indigo-100 text-indigo-700' },
  CHECKED_IN: { label: 'Hotel Check-in', color: 'bg-purple-100 text-purple-700' },
  IN_TREATMENT: { label: 'In Treatment', color: 'bg-cyan-100 text-cyan-700' },
  POST_OP: { label: 'Post-Op Care', color: 'bg-teal-100 text-teal-700' },
  COMPLETED: { label: 'Completed', color: 'bg-green-100 text-green-700' }
};

// P0-INTEGRITY: Removed all hardcoded mock patient data
// Representative data should come from backend API once this feature is fully built
// Currently shows empty state — no fake data
const initialAssignedPatients = [];

export const ClinicRepresentativeDataProvider = ({ children }) => {
  const [assignedPatients, setAssignedPatients] = useState(initialAssignedPatients);

  const getPatientsForRepresentative = useCallback((representativeId) => {
    return assignedPatients.filter(p => p.representativeId === representativeId);
  }, [assignedPatients]);

  const getTodaysArrivals = useCallback((representativeId) => {
    const today = new Date().toISOString().split('T')[0];
    return assignedPatients.filter(
      p => p.representativeId === representativeId && 
           p.travelDetails?.arrivalDate === today &&
           p.operationalStatus === 'ARRIVAL_PENDING'
    );
  }, [assignedPatients]);

  const getUpcomingPatients = useCallback((representativeId) => {
    const today = new Date();
    const twoWeeksLater = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
    const todayStr = today.toISOString().split('T')[0];
    const twoWeeksStr = twoWeeksLater.toISOString().split('T')[0];
    
    return assignedPatients.filter(p => {
      if (p.representativeId !== representativeId) return false;
      const arrivalDate = p.travelDetails?.arrivalDate;
      return arrivalDate && arrivalDate > todayStr && arrivalDate <= twoWeeksStr;
    });
  }, [assignedPatients]);

  const getActivePatients = useCallback((representativeId) => {
    const activeStatuses = ['ARRIVED', 'PICKED_UP', 'CHECKED_IN', 'IN_TREATMENT', 'POST_OP'];
    return assignedPatients.filter(
      p => p.representativeId === representativeId && activeStatuses.includes(p.operationalStatus)
    );
  }, [assignedPatients]);

  const updateOperationalStatus = useCallback((patientId, newStatus, updatedBy) => {
    setAssignedPatients(prev => prev.map(p => {
      if (p.id === patientId) {
        return {
          ...p,
          operationalStatus: newStatus,
          statusHistory: [
            ...(p.statusHistory || []),
            { status: newStatus, timestamp: new Date().toISOString(), updatedBy }
          ]
        };
      }
      return p;
    }));
  }, []);

  const addClinicNote = useCallback((patientId, text, createdBy) => {
    setAssignedPatients(prev => prev.map(p => {
      if (p.id === patientId) {
        return {
          ...p,
          clinicNotes: [
            ...(p.clinicNotes || []),
            {
              id: `note-${Date.now()}`,
              text,
              createdBy,
              createdAt: new Date().toISOString()
            }
          ]
        };
      }
      return p;
    }));
  }, []);

  const getPatientById = useCallback((patientId) => {
    return assignedPatients.find(p => p.id === patientId);
  }, [assignedPatients]);

  const getRepresentativeStats = useCallback((representativeId) => {
    const patients = assignedPatients.filter(p => p.representativeId === representativeId);
    return {
      total: patients.length,
      arrivalPending: patients.filter(p => p.operationalStatus === 'ARRIVAL_PENDING').length,
      inTreatment: patients.filter(p => ['IN_TREATMENT', 'ARRIVED', 'PICKED_UP', 'CHECKED_IN'].includes(p.operationalStatus)).length,
      postOp: patients.filter(p => p.operationalStatus === 'POST_OP').length,
      completed: patients.filter(p => p.operationalStatus === 'COMPLETED').length
    };
  }, [assignedPatients]);

  const value = {
    assignedPatients,
    getPatientsForRepresentative,
    getTodaysArrivals,
    getUpcomingPatients,
    getActivePatients,
    updateOperationalStatus,
    addClinicNote,
    getPatientById,
    getRepresentativeStats,
    OPERATIONAL_STATUSES
  };

  return (
    <ClinicRepresentativeDataContext.Provider value={value}>
      {children}
    </ClinicRepresentativeDataContext.Provider>
  );
};

export default ClinicRepresentativeDataContext;
